import {
    Box,
    Grid,
    Image,
    Menu,
    MenuButton,
    MenuList,
    SimpleGrid,
    Text,
    Tooltip,
    useDisclosure,
  } from "@chakra-ui/react";
  import {
    getQuickAccessBarApi,
    getQuickAccessBarUnreadCountApi,
    readCountApi,
  } from "api/deviceData";
  import { useAppSelector } from "hooks/store/hooks";
  import React, { useEffect, useState } from "react";
  import {
    Link,
    useLocation,
    useNavigate,
    useSearchParams,
  } from "react-router-dom";
  import { selectColorThemeMode } from "stores/ui/uiSlice";
  
  import {
    markAuthStore,
    selectCurrentDevice,
    selectCurrentDeviceStatusSettings,
    selectDeviceFamily,
  } from "stores/auth/authSlice";
  import {
    getCurrentDeviceDetails,
    getTempCurrentDeviceDetails,
    setQuickAccessView,
    setTempCurrentDeviceDetails,
  } from "utils/local-storage";
  import LightUPArrow from "components/Icons/QuickAccessBarIcons/LightUPArrow";
  import LightDOWNArrow from "components/Icons/QuickAccessBarIcons/LightDOWNArrow";
  import DarkDOWNArrow from "components/Icons/QuickAccessBarIcons/DarkDOWNArrow";
  import DarkUPArrow from "components/Icons/QuickAccessBarIcons/DarkUPArrow";
  import apiHelper from "services/apiHelper";
  import { useDispatch } from "react-redux";
  import DynamicTitleComponent from "components/DynamicTitleComponent";
  
  import { excludedViews, isexcludedpathname } from "configs/common";
  import { ASSETS, URLS } from "configs/brandConfig";
  import { useTranslation } from "TranslationContext";
  import RestrictedFeatureModel from "./RestrictedFeatureModel";
  
  const TopQuickAccessBar = () => {
    // Redux selectors for theme mode, device details, and family
    const colorThemeMode = useAppSelector(selectColorThemeMode);
    const currentDevice = useAppSelector(selectCurrentDevice);
    const deviceFamily = useAppSelector(selectDeviceFamily);
    const [searchParams] = useSearchParams();
    const {translate} = useTranslation();
    // Media query to check if the device is desktop
    // const [isDesktop] = useMediaQuery("(min-width: 768px)");
  
    // Hooks for location, navigation, and dispatch
    const { pathname } = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
  
    // State variables
    const [displayCount, setDisplayCount] = useState({});
    const [loading, setLoading] = useState(true);
    const [openQuickAccessBar, setOpenQuickAccessBar] = useState(false);
    const [quickAccessBarData, setQuickAccessBarData] = useState();
    const [quickAccessUnreadCountData, setQuickAccessUnreadCountData] =
      useState();
    const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  
    // Fetch local device and user details
    const localCurrentDevice = getCurrentDeviceDetails();
    const localTempCurrentDevice = getTempCurrentDeviceDetails();
    const currentDeviceStatusSettings = useAppSelector(
      selectCurrentDeviceStatusSettings
    );
    const canaccesslive = currentDeviceStatusSettings?.device_settings?.canaccesslive;
    const { isOpen, onOpen, onClose } = useDisclosure();
  
    // Effect to check IMEI status and navigate accordingly
    useEffect(() => {
      if (!localCurrentDevice?.family || !localCurrentDevice?.imei) return;
  
      (async () => {
        await apiHelper(
          `api/imei/updateimei/${localCurrentDevice.imei}/${localCurrentDevice.imei}`,
          "get"
        )
          .then((res) => {
            const type = res.data.data.license.type;
  
            // Handle different license and status conditions
            if (res.data.status === 203) {
              dispatch(markAuthStore({ appDisable: false }));
              navigate("/registerNewDevice");
            } else if (
              res.data.status === 402 ||
              !type ||
              ["3", "4"].includes(type)
            ) {
              dispatch(markAuthStore({ appDisable: true }));
              navigate("/subscription");
            } else {
              dispatch(markAuthStore({ appDisable: false }));
              if (pathname !== "/paymentSuccess") {
                // navigate("/dashboard");
              }
            }
          })
          .catch((err) => {
            dispatch(markAuthStore({ appDisable: true }));
            if (err?.response?.data?.status === 402) {
              navigate("/subscription");
            }
          });
      })();
  
      // Fetch quick access data if not already loaded
      if (!quickAccessBarData) {
        getQuickAccessBarApi(
          localCurrentDevice.imei,
          localCurrentDevice.family,
          setQuickAccessBarData
        );
      }
      if (!quickAccessUnreadCountData) {
        getQuickAccessBarUnreadCountApi(
          localCurrentDevice.imei,
          setQuickAccessUnreadCountData
        );
      }
    }, []);
  
    // Effect to update temp device details and fetch quick access data when device changes
    useEffect(() => {
      if (!currentDevice?.family || !currentDevice?.imei) return;
  
      if (localTempCurrentDevice.imei !== currentDevice.imei) {
        setTempCurrentDeviceDetails(currentDevice);
        getQuickAccessBarApi(
          currentDevice.imei,
          currentDevice.family,
          setQuickAccessBarData
        );
        getQuickAccessBarUnreadCountApi(
          currentDevice.imei,
          setQuickAccessUnreadCountData
        );
      }
    }, [currentDevice]);
  
    // Effect to manage loading state based on quick access data availability
    useEffect(() => {
      if (!quickAccessBarData) return;
  
      setLoading(!(deviceFamily === 12 || quickAccessUnreadCountData));
    }, [quickAccessBarData, quickAccessUnreadCountData]);
  
    // Function to mark an item as read and update display count state
    const callQuickAccessBarComponent = (id) => {
      if (id !== "audiovideo") {
        readCountApi(currentDevice.imei, id);
      }
      setDisplayCount({ ...displayCount, [id]: false }); // }
      setQuickAccessUnreadCountData(prev => ({...prev,[id]:0,}));
    };
  
    // Effect to calculate unread count and update display state
    useEffect(() => {
      let displayCountData = {};
      if (quickAccessBarData && quickAccessUnreadCountData) {
        quickAccessBarData.forEach((item) => {
          displayCountData[item.id] = quickAccessUnreadCountData[item.id] > 0;
        });
      }
      setDisplayCount({ ...displayCountData });
  
      // Calculate total unread messages count
      const total = Object.values(quickAccessUnreadCountData || {}).reduce(
        (acc, value) => acc + value,
        0
      );
      setTotalUnreadCount(total);
    }, [quickAccessBarData, quickAccessUnreadCountData]);
  
    const shouldRenderHeader =
      (!excludedViews.includes(searchParams.get("view")) &&
        !isexcludedpathname.includes(pathname)) ||
      localCurrentDevice?.family === 12;
    
  const featureMap = {
    calllog: "calls",
    instantphoto: "audiovideo",
    locatedevice: "remoteCommands",
    webhistory: "webHistory",
    webblocklist: "webFilter",
    payment: "subscription",
    dataliberation: "dataManagement",
    pkglist: "apps",
    Tiktok: "tiktok",
    Badoo: "badoo",
    JustTalk: "justtalk",
    BOTIM: "botim",
    MTeams: "mteams",
    Threema: "threema",
    YouTube: "youtube",
    Discord: "discord",
    Twitch: "twitch",
    Kakao:"kakao",
    directory:"sdCard"
};


  function isRestricted(appId) {
    const apiMatch = Object.keys(featureMap).find(key => featureMap[key].toLowerCase() === appId.toLowerCase());

  return (
    (currentDeviceStatusSettings?.device_settings?.restrictedfeature)?.split(",")?.includes(appId.toLowerCase()) ||   
    (apiMatch && (currentDeviceStatusSettings?.device_settings?.restrictedfeature).includes(apiMatch)) 
  );
}

    return (
      <>
        <DynamicTitleComponent
          title={`${
            totalUnreadCount === 0 ? "" : `(${totalUnreadCount})`
          } SpyOne App`}
        />{" "}
        <Menu
          matchWidth
          isLazy
          lazyBehavior="unmount"
          w="20px"
          mb={{ sm: "10px", md: "20px", base: "10px" }}
        >
          <MenuButton
            h={"51px"}
            display={"flex"}
            alignItems={"center"}
            w={"50px"}
            position="relative"
            _hover={{ cursor: "pointer" }}
            onClick={() => setOpenQuickAccessBar(!openQuickAccessBar)}
          >
            {totalUnreadCount === 0 ? (
              ""
            ) : (
              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={"center"}
                bg={"#f62d51"}
                minW={"20px"}
                w={"fit-content"}
                borderRadius={"50px"}
                position={"absolute"}
                top={"0px"}
                left={"20px"}
                p="3px 8px 2px"
              >
                <Text
                  fontSize={"10px"}
                  color={"#ffffff"}
                  m="0px"
                  lineHeight={"10px"}
                >
                  {totalUnreadCount}
                </Text>
              </Box>
            )}
  
            {openQuickAccessBar ? (
              colorThemeMode === "light" ? (
                <DarkUPArrow />
              ) : (
                  <LightDOWNArrow />
              )
            ) : colorThemeMode === "light" ? (
              <DarkDOWNArrow />
            ) : (
              <LightUPArrow />
            )}
          </MenuButton>
  
          <MenuList
            zIndex={1002}
              height={"auto"}
            overflow="scroll"
            right={"0px"}
            margin={"-85px"}
            position={"absolute"}
            //minW={"140px !important"}
            //maxW={"140px !important"}
            borderRadius={"2px"}
            marginTop={"-9px"}
            p={0}
            bg={`${colorThemeMode}_background`}
            boxShadow={"1px 1px 15px rgba(0, 0, 0, .1)"}
          >
            <SimpleGrid
              columns={{ base: 1 }}
              spacing={2}
              width={"100"}
            >
              <Box
                display={"flex"}
                flexWrap={"wrap"}
                justifyContent={"center"}
                h={"fit-content"}
                overflow={"hidden"}
                w={"-webkit-fill-available"}
              >
                {quickAccessBarData &&
                  (deviceFamily === 12 || quickAccessUnreadCountData) &&
                  quickAccessBarData
                    .filter((app) => ASSETS.ICONS.quickAccess[app.id])
                    .map((app) => {
                    return (
                      app?.id === "audiovideo" && canaccesslive === 0 ?(
                        <></>
                      ):(
                      <Grid key={app.name}>
                        {app.id === "help" ? (
                          <>
                            <Box p="8px 5px" as={Link} to={URLS.ANDROID10_SETTINGS} target="_blank">
                              <Tooltip
                                hasArrow
                                label={translate[app.name]}
                                color={"#ffffff"}
                                bg={"#000000"}
                                placement="right"
                                borderRadius={"4px"}
                                p={"5px"}
                              >
                                <Box
                                  display={"flex"}
                                  alignItems={"center"}
                                  position={"relative"}
                                >
                                  <Image
                                    borderRadius="full"
                                    boxSize="35px"
                                    // src={eval(app.id)}
                                    src={ASSETS.ICONS.quickAccess[app.id]}
                                    alt={translate[app.name]}
                                    mr="5px"
                                    bg={`${colorThemeMode}_background`}
                                    _hover={{ cursor: "pointer" }}
                                  />
                                </Box>
                              </Tooltip>
                            </Box>
                          </>
                        ) : (
                          <Box
                            p="8px 5px"
                            as={Link}
                            to={`/quickAccessBar?view=${app.id}`}
                            key={app.id}
                            onClick={(e) => {
                              if(isRestricted(app.id)){
                                e.preventDefault()
                                onOpen();
                              }else{
                                setQuickAccessView(app.id)}
                              }
                            }
                          >
                            <Tooltip
                              hasArrow
                              label={translate[app.name]}
                              color={"#ffffff"}
                              bg={"#000000"}
                              placement="right"
                              borderRadius={"4px"}
                              p={"5px"}
                            >
                              <Box
                                display={"flex"}
                                alignItems={"center"}
                                position={"relative"}
                              >
                                <Image
                                  borderRadius="full"
                                  boxSize="35px"
                                  // src={eval(app.id)}
                                  src={ASSETS.ICONS.quickAccess[app.id]}
                                  alt="Dan Abramov"
                                  mr="5px"
                                  bg={`${colorThemeMode}_background`}
                                  _hover={{ cursor: "pointer" }}
                                  onClick={() => {
                                    setOpenQuickAccessBar(false);
                                    callQuickAccessBarComponent(app?.id);
                                  }}
                                />
                                {displayCount[app.id] &&
                                  quickAccessUnreadCountData[app.id] > 0 && (
                                    <Box
                                      display={"flex"}
                                      alignItems={"center"}
                                      justifyContent={"center"}
                                      bg={"#f62d51"}
                                      minW={"20px"}
                                      w={"fit-content"}
                                      borderRadius={"50px"}
                                      position={"absolute"}
                                      top={"0px"}
                                      left={"20px"}
                                      p="3px 8px 2px"
                                    >
                                      <Text
                                        fontSize={"10px"}
                                        color={"#ffffff"}
                                        m="0px"
                                        lineHeight={"10px"}
                                      >
                                        {quickAccessUnreadCountData[app.id]}
                                      </Text>
                                    </Box>
                                  )}
                              </Box>
                            </Tooltip>
                          </Box>
                        )}
                      </Grid>
                      )
                    );
                  })}
              </Box>
            </SimpleGrid>
          </MenuList>
        </Menu>
        <RestrictedFeatureModel isOpen={isOpen} onClose={onClose} />
      </>
    );
  };
  
  export default TopQuickAccessBar;
  